// Decoded audio → a PCM WAV at the source's own depth. Pure.
//
// Browsers decode an N-bit integer sample to `int / 2^(N-1)`, so scaling back
// by exactly 2^(N-1) recovers the source's integers: a 16-bit FLAC becomes a
// 16-bit WAV with the very same samples — a lossless repackaging, not a
// conversion. (shared/audio/master-mix.ts's encoders scale by 2^(N-1) − 1,
// which is fine for a mix but nudges every sample of a source by a hair.)

import type { PcmSource } from "@web-shared/lib/wav";

export type WavDepth = 16 | 24;

export function encodePcmWav(source: PcmSource, bits: WavDepth): Uint8Array {
  const channels = Math.max(1, source.numberOfChannels);
  const bytes = bits / 8;
  const frames = source.length;
  const dataSize = frames * channels * bytes;
  const pad = dataSize & 1;
  const out = new Uint8Array(44 + dataSize + pad);
  const view = new DataView(out.buffer);
  const tag = (o: number, s: string): void => {
    for (let i = 0; i < 4; i++) out[o + i] = s.charCodeAt(i);
  };
  tag(0, "RIFF");
  view.setUint32(4, 36 + dataSize + pad, true);
  tag(8, "WAVE");
  tag(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, source.sampleRate, true);
  view.setUint32(28, source.sampleRate * channels * bytes, true);
  view.setUint16(32, channels * bytes, true);
  view.setUint16(34, bits, true);
  tag(36, "data");
  view.setUint32(40, dataSize, true);

  const scale = 2 ** (bits - 1);
  const max = scale - 1;
  const data = Array.from({ length: channels }, (_, c) => source.getChannelData(c));
  let o = 44;
  for (let f = 0; f < frames; f++) {
    for (let c = 0; c < channels; c++) {
      const v = Math.max(-scale, Math.min(max, Math.round(data[c]![f]! * scale)));
      if (bits === 16) {
        view.setInt16(o, v, true);
      } else {
        const u = v < 0 ? v + 0x1000000 : v;
        out[o] = u & 0xff;
        out[o + 1] = (u >> 8) & 0xff;
        out[o + 2] = (u >> 16) & 0xff;
      }
      o += bytes;
    }
  }
  return out;
}
